import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download, Monitor, Apple, Terminal, CheckCircle, Folder, FileText, Zap } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_docuflow-68/artifacts/y7jajers_Well%20Connected%20Logo_Feb2024_600px.jpg";

export default function DownloadPage() {
  const features = [
    { icon: Folder, title: "Local Folder Watching", desc: "Monitors your folders for new documents" },
    { icon: FileText, title: "Automatic OCR", desc: "AI-powered data extraction from invoices" },
    { icon: Zap, title: "Smart Renaming", desc: "Renames files based on extracted data" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="WELL Connected" className="h-12" />
          </Link>
          <Link to="/login">
            <Button variant="outline">Sign In</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-[#1a3a6e]">Desktop Client v1.0</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-[#1a3a6e] mb-4">
            Process Documents from Your Computer
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Download the WELL Connected Desktop Client to watch local folders and automatically 
            process your invoices with AI-powered OCR.
          </p>
        </div>
      </section>

      {/* Download Cards */}
      <section className="py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Windows */}
            <Card className="border-2 hover:border-[#1a3a6e] transition-colors">
              <CardHeader className="text-center">
                <div className="mx-auto mb-2 p-3 bg-blue-100 rounded-full w-fit">
                  <Monitor className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle>Windows</CardTitle>
                <CardDescription>Windows 10/11 (64-bit)</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button 
                  className="w-full bg-[#1a3a6e] hover:bg-[#0f2549]"
                  onClick={() => alert('Desktop client build coming soon! For now, build from source.')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download .exe
                </Button>
                <p className="text-xs text-muted-foreground mt-2">~85 MB</p>
              </CardContent>
            </Card>

            {/* macOS */}
            <Card className="border-2 hover:border-[#1a3a6e] transition-colors">
              <CardHeader className="text-center">
                <div className="mx-auto mb-2 p-3 bg-gray-100 rounded-full w-fit">
                  <Apple className="h-8 w-8 text-gray-700" />
                </div>
                <CardTitle>macOS</CardTitle>
                <CardDescription>macOS 11+ (Intel & Apple Silicon)</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button 
                  className="w-full bg-[#1a3a6e] hover:bg-[#0f2549]"
                  onClick={() => alert('Desktop client build coming soon! For now, build from source.')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download .dmg
                </Button>
                <p className="text-xs text-muted-foreground mt-2">~90 MB</p>
              </CardContent>
            </Card>

            {/* Linux */}
            <Card className="border-2 hover:border-[#1a3a6e] transition-colors">
              <CardHeader className="text-center">
                <div className="mx-auto mb-2 p-3 bg-orange-100 rounded-full w-fit">
                  <Terminal className="h-8 w-8 text-orange-600" />
                </div>
                <CardTitle>Linux</CardTitle>
                <CardDescription>Ubuntu, Debian, Fedora (64-bit)</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button 
                  className="w-full bg-[#1a3a6e] hover:bg-[#0f2549]"
                  onClick={() => alert('Desktop client build coming soon! For now, build from source.')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download .AppImage
                </Button>
                <p className="text-xs text-muted-foreground mt-2">~80 MB</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Why Use the Desktop Client?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="text-center p-6">
                <div className="mx-auto mb-4 p-3 bg-white rounded-full w-fit shadow-sm">
                  <feature.icon className="h-6 w-6 text-[#c41e3a]" />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
          <div className="space-y-4">
            {[
              "Download and install the desktop client for your operating system",
              "Launch the app and enter your license key to activate",
              "Configure your input, output, and failed folders",
              "Select your OCR template from the ones assigned to your company",
              "Click 'Start Watching' - the app will now monitor your input folder",
              "Drop documents in the input folder - they'll be automatically processed and renamed!"
            ].map((step, idx) => (
              <div key={idx} className="flex items-start gap-4 p-4 bg-white rounded-lg border">
                <div className="flex-shrink-0 w-8 h-8 bg-[#1a3a6e] text-white rounded-full flex items-center justify-center font-bold">
                  {idx + 1}
                </div>
                <p className="pt-1">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Requirements</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">System Requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>4 GB RAM minimum</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>500 MB free disk space</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Internet connection for activation</span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">License</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Valid WELL Connected license key</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Available device slot on your license</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Contact your admin for a license key</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t">
        <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
          <p>© 2024 WELL Connected Business Systems. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
